import React, { useContext, useEffect, useState } from 'react'
import {
  FeatherLoader,
  GoBackIcon,
  useErrorService,
  useNotificationService,
  mediaContent,
  programs as allPrograms,
} from '@abroad/components'
import { JourneyMediasContext } from '../../utils/contexts'
import API from '../../utils/API'

const matchesQuery = (text, query) => {
  if (!query) return true
  return String(text || '')
    .toLowerCase()
    .includes(query.toLowerCase())
}

const AudioLists = ({
  id,
  dropDownSelected,
  audiosList,
  currentCategorySelected,
  experienceChrome = false,
  searchQuery = '',
  preloadedTracks = null,
}) => {
  const { mediasList, setMediasList } = useContext(JourneyMediasContext)

  const [uniqueAudios, setUniqueAudios] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFilterDataLoading, setIsFilterDataLoading] = useState(true)
  const { showNotification } = useNotificationService()
  const { showError } = useErrorService()

  const filterMedias = (data = [], { applySearch = true } = {}) => {
    setIsFilterDataLoading(true)
    const getAllIds = mediasList?.map((audio) => audio?._id)
    let filteredData = data?.filter(
      (module) => !getAllIds.includes(module?._id),
    )
    const q = String(searchQuery || '').trim()
    if (applySearch && q) {
      filteredData = filteredData.filter((audio) =>
        matchesQuery(audio?.title, q),
      )
    }
    setUniqueAudios(filteredData)
    setIsFilterDataLoading(false)
  }

  useEffect(() => {
    if (Array.isArray(preloadedTracks)) {
      setIsLoading(false)
      // Parent already decided which tracks to show for this search.
      filterMedias(preloadedTracks, { applySearch: false })
      return
    }

    const updateDropdownOption = async () => {
      try {
        const { data } = await API.ClientAPI.programs.getModuleById(id)
        const newMappedMedias = data?.medias?.map((media) => ({
          ...media,
          imageFilename: data.imageFilename,
        }))
        filterMedias(
          newMappedMedias.filter((media) => media.type === mediaContent.AUDIO),
        )
      } catch (e) {
        showError(e)
      }
      setIsLoading(false)
    }
    if (dropDownSelected === 'module') {
      updateDropdownOption()
    } else {
      const filteredData = audiosList?.filter((e) => e.id === id)
      setIsLoading(false)
      const newMedias = filteredData[0]?.medias?.map((audio) => ({
        ...audio,
        categoryId: { ...audio.category },
        subCategoryId: { ...audio.subCategory },
      }))
      filterMedias(
        Array.isArray(newMedias) && newMedias?.length > 0 ? newMedias : [],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediasList, id, searchQuery, preloadedTracks])

  const addMediaHandler = async (mediaInfo, type) => {
    if ((mediasList || []).some((media) => media?._id === mediaInfo?._id)) {
      showNotification('Please provide unique audio.', 'danger')
      return
    }

    // Track is already covered if its parent module is attached.
    if (
      id &&
      (mediasList || []).some(
        (media) =>
          (media?._id === id || media?.mediaOrModuleId === id) &&
          media?.type === 'module',
      )
    ) {
      showNotification(
        'This track is already included via its module.',
        'danger',
      )
      return
    }

    let selectedMedia = { ...mediaInfo, mediaOrModuleId: mediaInfo._id, type }
    const filteredData = audiosList?.filter((e) => e.id === id)
    if (allPrograms[currentCategorySelected]) {
      selectedMedia.categoryId = {}
      selectedMedia.categoryId._id = allPrograms[currentCategorySelected]?.id
      selectedMedia.categoryId.title =
        allPrograms[currentCategorySelected]?.name
      selectedMedia.subCategoryId = filteredData[0]
    }

    setUniqueAudios((prev) => prev.filter((e) => e?._id !== mediaInfo?._id))
    setMediasList((prev) => [...prev, selectedMedia])
  }

  const emptyMessage = searchQuery
    ? 'No matching tracks in this module.'
    : experienceChrome
    ? 'All tracks from this module are attached individually.'
    : 'All the audios added already.'

  return (
    <div className={experienceChrome ? 'experience-lib-tracks-inner' : 'my-3'}>
      {(isLoading || isFilterDataLoading) && (
        <div className='d-flex justify-content-center align-items-center py-3'>
          <FeatherLoader />
        </div>
      )}
      {!isLoading && !isFilterDataLoading && uniqueAudios?.length === 0 && (
        <div
          className={
            experienceChrome
              ? 'experience-lib-empty'
              : 'd-flex justify-content-center align-items-center py-3'
          }>
          <p className='text-center mb-0'>{emptyMessage}</p>
        </div>
      )}
      {!isLoading && !isFilterDataLoading && uniqueAudios?.length > 0 && (
        <div className={experienceChrome ? '' : 'mr-3 ml-5'}>
          {uniqueAudios?.map((audio) =>
            experienceChrome ? (
              <div
                className='experience-lib-track'
                key={audio?._id || audio?.id}>
                <span className='copy'>
                  <b>{audio?.title}</b>
                </span>
                <button
                  type='button'
                  className='experience-lib-track-add'
                  onClick={() => addMediaHandler(audio, 'media')}>
                  Add
                </button>
              </div>
            ) : (
              <div
                className='bg-white p-2 d-flex border-radius-5 mb-2 cursor-pointer'
                key={audio?._id || audio?.id}>
                <GoBackIcon
                  className='media-remove-icon add-medias align-self-center mr-20'
                  onClick={() => addMediaHandler(audio, 'media')}
                />
                <p className='font-open-sans font-12 font-semibold tracking-normal leading-5 mb-0'>
                  {audio?.title}
                </p>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default AudioLists
